import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, navigate, useQueryParams } from 'raviger';
import { Button, Col, Card, Collapse, ListGroup, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typeahead } from 'react-bootstrap-typeahead';
import {
  faDiamondExclamation,
  faSquareChevronRight,
  faSquareChevronLeft,
  faTriangleExclamation,
  faCalendarExclamation,
  faCalendarClock,
  faChevronDoubleUp,
  faChevronDoubleDown,
} from '@fortawesome/pro-solid-svg-icons';
import { faPipe } from '@fortawesome/pro-thin-svg-icons';
import Select from 'react-select';
import Timeline, {
  TimelineHeaders,
  TimelineMarkers,
  CustomMarker,
  SidebarHeader,
  DateHeader
} from 'react-calendar-timeline';
import { titleCase } from '../components/Utils';
import { capitalize } from '../utils/formatString';
import Header from '../components/Header';
import Scrollbar from '../components/Scrollbars';
import PatientCalendarView from './components/PatientCalendarView';
import { DateRangePicker } from '../components/Form';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';
import { vetFilterStatusChoices } from '../animals/constants';
import { faChevronCircleDown, faChevronCircleUp, faChevronDown, faCircle } from '@fortawesome/free-solid-svg-icons';

const customStyles = {
  // For the select it self, not the options of the select
  control: (styles, { isDisabled}) => {
    return {
      ...styles,
      color: '#FFF',
      cursor: isDisabled ? 'not-allowed' : 'default',
      backgroundColor: isDisabled ? '#DFDDDD' : 'white',
      height: 35,
      minHeight: 35
    }
  },
  option: provided => ({
    ...provided,
    color: 'black'
  }),
  singleValue: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? '#595959' : 'black'
  }),
  menu: provided  => ({ ...provided, zIndex: 9999 })
};

const ToggleButton = ({ isOpen, onClick }) => (
  <span
    style={{zIndex:"11", marginLeft:"-25px"}}
    className="toggle-button"
    onClick={onClick}
    onMouseDown={(e) => {
      // Prevent input from losing focus.
      e.preventDefault();
    }}>
    {<>
      <FontAwesomeIcon icon={faPipe} style={{zIndex:"9999", height:"20px", marginLeft:"1px"}} transform={'grow-1 down-8 left-12'} color="#cccccc" size="sm" />
      <FontAwesomeIcon icon={faChevronDown} style={{zIndex:"9999", WebkitTextStroke: '0.5px', fontWeight:"bold"}} transform={'down-8 left-4'} color="#cccccc" size="sm" />
    </>}
  </span>
);

function Vet({ incident, organization }) {

  const [calendarData, setCalendarData] = useState([]);

  // 1. Define your custom group renderer
  const renderGroup = ({ group }) => {
    const hasItemsToday = calendarData.filter(item => dayjs(item.start_time).isSame(dayjs(), 'day')).some(item => Number(item.group) === Number(group.id));
    return (
      <div className="custom-group-title">
        <Link href={"/" + organization + "/" + incident + "/vet/medrecord/" + group.medical_record} className="underline-link" style={{color:"white", textDecoration: "none"}}>
          {group.title}
        </Link>
        {group.overdue_treatment || group.overdue_diagnostic || group.overdue_procedure ? 
          <OverlayTrigger
            key={"overdue"}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-overdue`}>
                This patient has overdue veterinary tasks.
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faCalendarExclamation} style={{ color: "#ff4c4c" }} className="ms-1" />
          </OverlayTrigger>
        : ""}
        {!hasItemsToday && group.underdue_procedure ? 
          <OverlayTrigger
            key={"underdue"}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-underdue`}>
                This patient has a veterinary task next due on {dayjs(group.underdue_procedure).format('MMMM D, YYYY')}.
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faCalendarClock} className="ms-1" />
          </OverlayTrigger>
        : ""}
      </div>
    );
  };

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({vet_requests:[], treatments:[], diagnostics:[], procedures:[], isFetching: true});
  const [vetStatus, setVetStatus] = useState(null);
  const [vetType, setVetType] = useState(null);
  const [speciesCategory, setSpeciesCategory] = useState([]);
  const [shelterOptions, setShelterOptions] = useState([]);
  const [selectedShelter, setSelectedShelter] = useState('all');
  const [speciesChoices, setSpeciesChoices] = useState([]);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [animalData, setAnimalData] = useState({});
  const [groupData, setGroupData] = useState([]);
  const [filteredAnimalData, setFilteredAnimalData] = useState([]);
  const [activeOrders, setActiveOrders] = useState('vet_requests');
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({'vet_status':null, 'vet_type':null, 'species_category':[], 'shelter':'all'});
  const [showCalendar, setShowCalendar] = useState(true);
  const [showRequests, setShowRequests] = useState(false);
  const [currentDate, setCurrentDate] = useState(dayjs().toDate());

  const lastItemEndTimes = useMemo(() => {
    const mappings = {};
    groupData.forEach(group => {
      const groupItems = calendarData.filter(item => Number(item.group) === Number(group.id));
      if (groupItems.length > 0) {
        // Get the maximum end_time in this group
        mappings[group.id] = Math.max(...groupItems.map(i => i.end_time));
      } else {
        // If the group has no items, start coloring from the very beginning
        mappings[group.id] = dayjs(currentDate).startOf('day').valueOf();
      }
    });
    return mappings;
  }, [groupData, calendarData]);

  const firstItemStartTimes = useMemo(() => {
    const mappings = {};
    groupData.forEach(group => {
      const groupItems = calendarData.filter(item => Number(item.group) === Number(group.id));
      if (groupItems.length > 0) {
        // Get the min start_time in this group
        mappings[group.id] = Math.min(...groupItems.map(i => i.start_time));
      } else {
        // If the group has no items, start coloring from the very beginning
        mappings[group.id] = dayjs(currentDate).startOf('day').valueOf();
      }
    });
    return mappings;
  }, [groupData, calendarData]);

  const handleItemClick = (itemId, e, time) => {
    let group = groupData.filter(group => Number(group.id) === Number(calendarData.filter(item => Number(item.id) === Number(itemId))[0].group))[0];
    setSelectedAnimal(animalData[group.id]);
  };

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchData = async () => {
      setData(prevState => ({ ...prevState, "isFetching":true}));
      // Fetch Shelter data.
      await axios.get('/vet/api/vetrequest/?incident=' + state.incident.id + '&open=true', {
        cancelToken: source.token,
      })
      .then(async (vetResponse) => {
        if (!unmounted) {
          let animal_data = {};
          let calendar_data = [];
          let id_tracker = 0;
          let time_tracker = {};
          const [treatmentResponse, diagnosticResponse, procedureResponse] = await Promise.all([
            axios.get('/vet/api/treatmentplan/?incident=' + state.incident.id + '&open=true'),
            axios.get('/vet/api/diagnosticresults/?incident=' + state.incident.id + '&open=true'),
            axios.get('/vet/api/procedureresults/?incident=' + state.incident.id + '&open=true')
          ]);
          let vet_request_count = {'avian':0, 'cat':0, 'dog':0, 'camelid':0, 'small_mammals':0, 'reptile/amphibian':0, 'equine':0, 'ruminant':0, 'swine':0, 'other':0};
          vetResponse.data.results.forEach(vet_request => {
            if (Object.keys(animal_data).includes(String(vet_request.animal_object.id))) {
              animal_data[vet_request.animal_object.id]['vet_request'] = animal_data[vet_request.animal_object.id]['vet_request'] + 1;
            }
            else {
              animal_data[vet_request.animal_object.id] = vet_request.animal_object;
              animal_data[vet_request.animal_object.id]['vet_request'] = 1;
            }
            vet_request_count[vet_request.animal_object.category] += 1;
          })
          setShowRequests(vetResponse.data.results.length > 0);
          let treatment_count = {'avian':0, 'cat':0, 'dog':0, 'camelid':0, 'small_mammals':0, 'reptile/amphibian':0, 'equine':0, 'ruminant':0, 'swine':0, 'other':0};
          let track_plans = [];
          treatmentResponse.data.forEach(treatment => {
            let overdue_treatment = false;
            let underdue_treatment = null;
            treatment.treatment_requests.filter(treatment_request => !treatment_request.actual_admin_time && !treatment_request.not_administered).forEach(treatment_request => {
              if (Object.keys(time_tracker).includes(String(treatment.animal_object.id) + String(dayjs(treatment_request.suggested_admin_time).startOf('hour').format('DDHH')))) {
                time_tracker[String(treatment.animal_object.id) + String(dayjs(treatment_request.suggested_admin_time).startOf('hour').format('DDHH'))] = Number(time_tracker[String(treatment.animal_object.id) + String(dayjs(treatment_request.suggested_admin_time).startOf('hour').format('DDHH'))]) + 1;
              }
              else {
                time_tracker[String(treatment.animal_object.id) + String(dayjs(treatment_request.suggested_admin_time).startOf('hour').format('DDHH'))] = 1;
              }
              calendar_data.push({id:id_tracker, group:treatment.animal_object.id, vet_type:'treatments', title:time_tracker[String(treatment.animal_object.id) + String(dayjs(treatment_request.suggested_admin_time).startOf('hour').format('DDHH'))], start_time: dayjs(treatment_request.suggested_admin_time).startOf('hour').add(2, 'minute'), end_time: dayjs(treatment_request.suggested_admin_time).startOf('hour').add(1, 'hour'),
                itemProps: {
                  style: {
                    background: dayjs(treatment_request.suggested_admin_time).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                    color: 'white',       // Text color
                    zIndex: 41,
                    borderColor: dayjs(treatment_request.suggested_admin_time).isBefore(dayjs()) ? 'black' : 'black'  // Border color
                  }
                }
              });
              id_tracker = id_tracker + 1;
              if (dayjs(treatment_request.suggested_admin_time).isBefore(dayjs())) {
                overdue_treatment = true;
              }
              if (dayjs(treatment_request.suggested_admin_time).isAfter(dayjs())) {
                underdue_treatment = underdue_treatment ? (dayjs(treatment_request.suggested_admin_time).isAfter(dayjs(underdue_treatment)) ? treatment_request.suggested_admin_time : underdue_treatment) : treatment_request.suggested_admin_time;
              }
            });

            if (!track_plans.includes(treatment.id)) {
              if (Object.keys(animal_data).includes(String(treatment.animal_object.id))){
                animal_data[treatment.animal_object.id]['treatment'] = (animal_data[treatment.animal_object.id]['treatment'] ? animal_data[treatment.animal_object.id]['treatment'] : 0) + 1;
                animal_data[treatment.animal_object.id]['overdue_treatment'] = animal_data[treatment.animal_object.id]['overdue_treatment'] || overdue_treatment;
              }
              else {
                animal_data[treatment.animal_object.id] = treatment.animal_object;
                animal_data[treatment.animal_object.id]['treatment'] = 1;
                animal_data[treatment.animal_object.id]['overdue_treatment'] = overdue_treatment;
              }
              treatment_count[treatment.animal_object.category] += 1;
              track_plans.push(treatment.id)
            }
          });
          let diagnostic_count = {'avian':0, 'cat':0, 'dog':0, 'camelid':0, 'small_mammals':0, 'reptile/amphibian':0, 'equine':0, 'ruminant':0, 'swine':0, 'other':0};
          diagnosticResponse.data.results.forEach(diagnostic => {
            let overdue_diagnostic = false;
            let underdue_procedure = null;
            if (dayjs(diagnostic.open).isBefore(dayjs())) {
              overdue_diagnostic = true;
            }
            if (Object.keys(animal_data).includes(String(diagnostic.animal_object.id))){
              animal_data[diagnostic.animal_object.id]['diagnostic'] = (animal_data[diagnostic.animal_object.id]['diagnostic'] ? animal_data[diagnostic.animal_object.id]['diagnostic'] : 0) + 1;
              animal_data[diagnostic.animal_object.id]['overdue_diagnostic'] = animal_data[diagnostic.animal_object.id]['overdue_diagnostic'] || overdue_diagnostic;
            }
            else {
              animal_data[diagnostic.animal_object.id] = diagnostic.animal_object;
              animal_data[diagnostic.animal_object.id]['diagnostic'] = 1;
              animal_data[diagnostic.animal_object.id]['overdue_diagnostic'] = overdue_diagnostic;
            }
            diagnostic_count[diagnostic.animal_object.category] += 1;
            if (Object.keys(time_tracker).includes(String(diagnostic.animal_object.id) + String(dayjs(diagnostic.open).startOf('hour').format('DDHH')))) {
              time_tracker[String(diagnostic.animal_object.id) + String(dayjs(diagnostic.open).startOf('hour').format('DDHH'))] = time_tracker[String(diagnostic.animal_object.id) + String(dayjs(diagnostic.open).startOf('hour').format('DDHH'))] + 1;
            }
            else {
              time_tracker[String(diagnostic.animal_object.id) + String(dayjs(diagnostic.open).startOf('hour').format('DDHH'))] = 1;
            }
            calendar_data.push({id:id_tracker, group:diagnostic.animal_object.id, vet_type:'diagnostics', title:time_tracker[String(diagnostic.animal_object.id) + String(dayjs(diagnostic.open).startOf('hour').format('DDHH'))], start_time: dayjs(diagnostic.open).startOf('hour').add(2, 'minute'), end_time: dayjs(diagnostic.open).startOf('hour').add(1, 'hour'),
              itemProps: {
                style: {
                  background: dayjs(diagnostic.open).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                  color: 'white',       // Text color
                  borderColor: dayjs(diagnostic.open).isBefore(dayjs()) ? 'black' : 'black'  // Border color
                }
              }
            });
            id_tracker = id_tracker + 1;
          });
          let procedure_count = {'avian':0, 'cat':0, 'dog':0, 'camelid':0, 'small_mammals':0, 'reptile/amphibian':0, 'equine':0, 'ruminant':0, 'swine':0, 'other':0};
          procedureResponse.data.results.forEach(procedure => {
            let overdue_procedure = false;
            let underdue_procedure = null;
            if (dayjs(procedure.open).isBefore(dayjs())) {
              overdue_procedure = true;
            }
            if (dayjs(procedure.open).isAfter(dayjs())) {
              underdue_procedure = underdue_procedure ? dayjs(procedure.open).isAfter(dayjs(underdue_procedure)) ? dayjs(procedure.open) : dayjs(underdue_procedure) : dayjs(procedure.open)
            }
            if (Object.keys(animal_data).includes(String(procedure.animal_object.id))){
              animal_data[procedure.animal_object.id]['procedure'] = (animal_data[procedure.animal_object.id]['procedure'] ? animal_data[procedure.animal_object.id]['procedure'] : 0) + 1;
              animal_data[procedure.animal_object.id]['overdue_procedure'] = animal_data[procedure.animal_object.id]['overdue_procedure'] || overdue_procedure;
              animal_data[procedure.animal_object.id]['underdue_procedure'] = animal_data[procedure.animal_object.id]['underdue_procedure'] || underdue_procedure;
            }
            else {
              animal_data[procedure.animal_object.id] = procedure.animal_object;
              animal_data[procedure.animal_object.id]['procedure'] = 1;
              animal_data[procedure.animal_object.id]['overdue_procedure'] = overdue_procedure;
              animal_data[procedure.animal_object.id]['underdue_procedure'] = underdue_procedure;
            }
            procedure_count[procedure.animal_object.category] += 1;
            if (Object.keys(time_tracker).includes(String(procedure.animal_object.id) + String(dayjs(procedure.open).startOf('hour').format('DDHH')))) {
              time_tracker[String(procedure.animal_object.id) + String(dayjs(procedure.open).startOf('hour').format('DDHH'))] = time_tracker[String(procedure.animal_object.id) + String(dayjs(procedure.open).startOf('hour').format('DDHH'))] + 1;
            }
            else {
              time_tracker[String(procedure.animal_object.id) + String(dayjs(procedure.open).startOf('hour').format('DDHH'))] = 1;
            }
            calendar_data.push({id:id_tracker, group:procedure.animal_object.id, vet_type:'procedures', title:time_tracker[String(procedure.animal_object.id) + String(dayjs(procedure.open).startOf('hour').format('DDHH'))], start_time: dayjs(procedure.open).startOf('hour').add(2, 'minute'), end_time: dayjs(procedure.open).startOf('hour').add(1, 'hour'),
              itemProps: {
                style: {
                  background: dayjs(procedure.open).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                  color: 'white',       // Text color
                  borderColor: dayjs(procedure.open).isBefore(dayjs()) ? 'black' : 'black'  // Border color
                }
              }
            });
            id_tracker = id_tracker + 1;
          });
          setShowCalendar(treatmentResponse.data.length > 0 || diagnosticResponse.data.results.length > 0 || procedureResponse.data.results.length > 0);
          setData({vet_requests:vetResponse.data.results, treatments:treatmentResponse.data, diagnostics:diagnosticResponse.data.results, procedures:procedureResponse.data.results, isFetching: false});
          setCalendarData(calendar_data);
          let group_data = [];
          let shelter_data = {};

          Object.entries(animal_data).forEach(([animal_id, animal_data]) => {
            // Add animals to group if they have a treatment/diagnostic/procedure.
            if (animal_data.treatment || animal_data.diagnostic || animal_data.procedure) {
              group_data.push({id:animal_id, id_for_incident:animal_data.id_for_incident, species_category:animal_data.category, title:'A#' + animal_data.id_for_incident + ' - ' + (animal_data.name || "Unknown Name") + " - " + titleCase(animal_data.species_string_singular), medical_record:animal_data.medical_record, vet_status:animal_data.vet_status, shelter:animal_data.shelter, overdue_treatment:animal_data.overdue_treatment, overdue_diagnostic:animal_data.overdue_diagnostic, overdue_procedure:animal_data.overdue_procedure, underdue_procedure:animal_data.underdue_procedure});
            }

            // If the animal has a shelter, add it to the count.
            if (animal_data.shelter) {
              if (shelter_data[animal_data.shelter]) {
                shelter_data[animal_data.shelter]['animal_count'] = shelter_data[animal_data.shelter].animal_count + animal_data.animal_count;
              }
              else {
                shelter_data[animal_data.shelter] = {id:animal_data.shelter, name:animal_data.shelter_object.name, animal_count:animal_data.animal_count}
              }
            }
            // Otherwise add it to field.
            else {
              if (shelter_data['field']) {
                shelter_data['field']['animal_count'] = shelter_data['field'].animal_count + animal_data.animal_count;
              }
              else {
                shelter_data['field'] = {id:'field', name:'Field', animal_count:animal_data.animal_count}
              }
            }
          });
          setGroupData(group_data);
          setAnimalData(animal_data);
          let shelter_options = [];
          let count = 0;
          Object.entries(shelter_data).forEach(([key, shelter]) => {
            shelter_options.push({value:shelter.id, label:shelter.name + ' (' + shelter.animal_count + ' Animals)'});
            count = count + shelter.animal_count;
          })
          shelter_options.unshift({value:'all', label:'All (' + count + ' Animals)'});
          setShelterOptions(shelter_options);
          let selected_shelter = shelter_options.filter(option => option.value === selectedShelter)[0]?.value;
          if (selected_shelter === 'all') {
            setFilteredAnimalData(group_data);
          }
          else if (selected_shelter === 'field') {
            setFilteredAnimalData(group_data.filter(animal => animal.shelter === null));
          }
          else if (selected_shelter) {
            setFilteredAnimalData(group_data.filter(animal => animal.shelter === selected_shelter));
          }
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };

    fetchData();

    const fetchSpecies = () => {
      setSpeciesChoices([]);
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let species_options = [];
          Array.from(new Set(response.data.map(result => result.category))).forEach(result => {
            species_options.push({value: result, label: capitalize(result)});
          })
          setSpeciesChoices(species_options.sort(function (a,b) {if (a.value < b.value){ return -1} if (a.value > b.value) {return 1} return 0;}).filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i)); //.sort
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [incident, organization]);

  // Hook handling option changes.
  useEffect(() => {
    let group_data_copy = [...groupData]
    group_data_copy = group_data_copy.filter(group => filterOptions.shelter === 'all' ? group : filterOptions.shelter === 'field' ? group.shelter === null : Number(filterOptions.shelter) === Number(group.shelter) ? group : null);
    group_data_copy = group_data_copy.filter(group => filterOptions.species_category.length > 0 ? filterOptions.species_category.includes(group.species_category) : group);
    group_data_copy = group_data_copy.filter(group => filterOptions.vet_status ? filterOptions.vet_status === group.vet_status : group)
    setFilteredAnimalData(group_data_copy);
  }, [filterOptions]);

  const [canvasWidth, setCanvasWidth] = useState(0);

  // Function to calculate exact width of the scrollable canvas container
  const updateCanvasWidth = (canvasTimeStart, canvasTimeEnd) => {
    const scrollContainer = document.querySelector('.rct-scroll');
    if (scrollContainer) {
      // This retrieves the actual width of the canvas currently rendered
      setCanvasWidth(scrollContainer.clientWidth);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      updateCanvasWidth();
    }, 1000)

    // Listen for resize changes affecting the timeline canvas layout
    window.addEventListener('resize', updateCanvasWidth);
    return () => window.removeEventListener('resize', updateCanvasWidth);
  }, []);

  return (
    <>
    <Header>Veterinary Medicine</Header>
    {selectedAnimal ? 
    <PatientCalendarView incident={incident} organization={organization} id={selectedAnimal.medical_record} selectedAnimal={selectedAnimal} setSelectedAnimal={setSelectedAnimal} currentDate={dayjs(currentDate)} />
    :
    <div className="row">
      <div className="col-12 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginTop:"-10px"}}>
            <div className="d-flex justify-content-between" style={{marginBottom:"-3px"}}>
              <h4 className="h4 mb-0 pb-0 pt-2 w-100">
                Open Treatments/Diagnostics/Procedures
                <FontAwesomeIcon icon={faChevronCircleUp} hidden={!showCalendar} onClick={() => {setShowCalendar(false)}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                <FontAwesomeIcon icon={faChevronCircleDown} hidden={showCalendar} onClick={() => {setShowCalendar(true)}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                <Button variant="outline-light" className="rounded float-end mr-auto" onClick={() => setShowFilters(!showFilters)} style={{height:"38px", color:"white"}}>Filters {showFilters ? <FontAwesomeIcon icon={faChevronDoubleUp} className="fa-move-up" size="sm" /> : <FontAwesomeIcon icon={faChevronDoubleDown} className="fa-move-up" size="sm" />}</Button>
              </h4>
            </div>
            <Collapse in={showFilters} className="mt-3">
              <div>
                <Card className="border rounded d-flex" style={{width:"100%"}}>
                  <Card.Body style={{marginTop:"-10px"}}>
                    <Row className="d-flex gx-2">
                      <Col xs={6}>
                        <label className="mb-2">Location</label>
                        <Select
                          id="shelter"
                          type="text"
                          name="shelter"
                          options={shelterOptions}
                          styles={customStyles}
                          isDisabled={data.isFetching}
                          value={shelterOptions?.filter(option => option.value === selectedShelter)[0]}
                          onChange={(instance) => {
                            setSelectedShelter(instance === null ? '' : instance.value);
                            setFilterOptions(prevState => ({ ...prevState, "shelter":instance?.value || ''}))
                          }}
                        />
                      </Col>
                      <Col xs={6}>
                        <label className="mb-2">Date Selection</label>
                        <DateRangePicker
                          name={`current_date`}
                          id={`current_date`}
                          mode="single"
                          style={{height:"35px"}}
                          data-enable-time={false}
                          clearable={'false'}
                          value={currentDate || null}
                          onChange={([date], dateStr) => {
                            setCurrentDate(date)
                          }}
                          disabled={data.isFetching}
                        />
                      </Col>
                    </Row>
                    <Row className="mt-3 d-flex gx-2">
                      <Col xs={6}>
                        <label className="mb-2">Vet Status</label>
                        <Select
                          id="vet_status"
                          name="vet_status"
                          styles={customStyles}
                          onChange={(instance) => {
                            setVetStatus(instance === null ? '' : instance.value);
                            setFilterOptions(prevState => ({ ...prevState, "vet_status":instance?.value || null}));
                          }}
                          isClearable={true}
                          isDisabled={data.isFetching}
                          options={vetFilterStatusChoices}
                          placeholder="Filter by Patient Vet Status..."
                        />
                      </Col>
                      <Col xs={6}>
                        <label className="mb-2">Vet Task Type</label>
                        <Select
                          id="vet_task"
                          name="vet_task"
                          styles={customStyles}
                          // onChange={(values) => {
                          //   setOptions({...options, vet_status: values.map(option => option.value)});
                          // }}
                          onChange={(instance) => {
                            setVetType(instance === null ? '' : instance.value);
                            // if (instance?.value === 'all') {
                              
                            // }
                            // console.log(calendarData)
                            // console.log(groupData)
                            if (instance?.value) {
                              // setFilteredAnimalData(groupData.filter(animal => animal.vet_status === instance?.value));
                            }
                            // else if (instance?.value) {
                            //   setFilteredAnimalData(groupData.filter(animal => animal.shelter === instance?.value));
                            // }
                            else {
                              // setFilteredAnimalData(groupData);
                            }
                          }}
                          isClearable={true}
                          isDisabled={true || data.isFetching}
                          options={[{value:'treatments', label:'Treatments'}, {value:'diagnostics', label:'Diagnostics'}, {value:'procedures', label:'Procedures'}]}
                          placeholder="Filter by Vet Task Type..."
                        />
                      </Col>
                    </Row>
                    <Row className="mt-3 d-flex gx-2">
                      <Col xs={12}>
                        <label className="mb-2">Species Category</label>
                        <Typeahead
                          id="species"
                          name="species"
                          multiple
                          onChange={(values) => {
                            setSpeciesCategory(values.map(option => option.value));
                            setFilterOptions(prevState => ({ ...prevState, "species_category":values.map(option => option.value)}));
                          }}
                          options={speciesChoices}
                          placeholder="Filter by Patient Species Category..."
                          className="species-typeahead mb-2 no-wrap-container w-100"
                          selected={speciesChoices.filter(choice => speciesCategory.includes(choice.value))}
                          inputProps={{ className: 'custom-typeahead-input' }}
                        >
                          {({ isMenuShown, toggleMenu }) => (
                            <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                          )}
                        </Typeahead>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>
            <Collapse in={showCalendar}>
              <div>
                <p className="phr" />
                <Timeline
                  groups={filteredAnimalData}
                  items={calendarData}
                  onItemSelect={handleItemClick}
                  onItemClick={handleItemClick}
                  buffer={1}
                  canMove={false}
                  canResize={false}
                  canChangeGroup={false}
                  onBoundsChange={updateCanvasWidth} 
                  defaultTimeStart={dayjs(currentDate).startOf('day')}
                  defaultTimeEnd={dayjs(currentDate).endOf('day')}
                  visibleTimeStart={dayjs(currentDate).startOf('day')}
                  visibleTimeEnd={dayjs(currentDate).endOf('day')}
                  groupRenderer={renderGroup}
                  sidebarWidth={215}
                >
                  <TimelineHeaders>
                    <DateHeader
                      unit="day"
                      intervalRenderer={({ getIntervalProps, intervalContext }) => {
                        const { key, ...intervalProps } = getIntervalProps();
                        return (
                        <div key={key}>
                          <div {...intervalProps} style={{
                            ...intervalProps.style,
                            display: 'flex',
                            justifyContent: 'flex-start', // Align content to the far left
                            paddingLeft: '2px',          // Add spacing from the edge
                          }}>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-previous-day`}>
                                  Go to previous day.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faSquareChevronLeft} onClick={() => {setCurrentDate(dayjs(currentDate).add(-1, 'day').toDate())}} size="2x" style={{ cursor:'pointer', marginTop:"0px", zIndex:"41" }} inverse />
                            </OverlayTrigger>
                          </div>
                          <div className="float-end" style={{
                            ...intervalProps.style,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'end',
                            cursor: 'pointer',          // Add spacing from the edge
                            paddingRight: '4px',
                          }}>
                            <OverlayTrigger
                              key={"next-day"}
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-next-day`}>
                                  Go to next day.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faSquareChevronRight} onClick={() => {setCurrentDate(dayjs(currentDate).add(1, 'day').toDate())}} size="2x" style={{ cursor:'pointer', marginTop:"0px", zIndex:"41" }} inverse />
                            </OverlayTrigger>
                          </div>
                          <span className="" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <span style={{marginTop:"4px"}}>{data.isFetching ? "Loading..." : dayjs(currentDate).format('dddd, MMMM D, YYYY')}</span>
                          </span>
                        </div>
                        );
                      }}
                    />
                    <DateHeader
                      unit="hour"
                      style={{marginLeft:"-1px"}}
                    />
                  </TimelineHeaders>
                  <TimelineMarkers>
                    {canvasWidth && groupData.map((group, index) => {
                      const lastEnd = lastItemEndTimes[group.id];
                      return (
                        <CustomMarker key={Math.random()} date={lastEnd}>
                          {({ styles }) => {
                            const totalCanvasDuration = dayjs(currentDate).endOf('day').valueOf() - dayjs(currentDate).startOf('day').valueOf();
                            const pxPerMs = canvasWidth / totalCanvasDuration;

                            // 2. Compute the distance in milliseconds from the marker to the end of the visible view
                            const durationToEndOfView = dayjs(currentDate).endOf('day').valueOf() - lastEnd;

                            // 3. Convert duration to pixels
                            const widthInPixels = durationToEndOfView * pxPerMs;

                            // Ensure we don't return negative widths if the marker is off-screen past the right side
                            const safeWidth = Math.max(0, widthInPixels);

                            // 2. Adjust styles to turn the marker vertical line into a block
                            const customStyles = {
                              ...styles,
                              backgroundColor: 'rgba(120, 116, 116, 0.65)', // Your empty background color
                              top: `${index * 30}px`,           // Align vertically with the row
                              height: `${30}px`,
                              width: `${safeWidth}px`,
                              pointerEvents: 'none',                   // Ensure clicks still pass through to canvas
                              zIndex: 0                                // Keep it underneath timeline items
                            };
                            return <div style={customStyles} />;
                          }}
                        </CustomMarker>
                      );
                    })}
                    {canvasWidth && groupData.map((group, index) => {
                      const lastEnd = lastItemEndTimes[group.id];
                      return (
                        <>
                        {dayjs(lastEnd).isBefore(dayjs(currentDate), 'day') ? <CustomMarker key={Math.random()} date={dayjs(currentDate).valueOf()}>
                          {({ styles }) => {
                            // 2. Adjust styles to turn the marker vertical line into a block
                            const customStyles = {
                              ...styles,
                              backgroundColor: 'rgba(120, 116, 116, 0.65)', // Your empty background color
                              top: `${index * 30}px`,           // Align vertically with the row
                              height: `${30}px`,
                              left: `0px`,
                              width: `${canvasWidth}px`, 
                              pointerEvents: 'none',                   // Ensure clicks still pass through to canvas
                              zIndex: 0                                // Keep it underneath timeline items
                            };
                            return <div style={customStyles} />;
                          }}
                        </CustomMarker> : ""}
                        </>
                      );
                    })}
                    {canvasWidth && groupData.map((group, index) => {
                      const firstStart = dayjs(firstItemStartTimes[group.id]).valueOf();
                      
                      const totalCanvasDuration = dayjs(currentDate).endOf('day').valueOf() - dayjs(currentDate).startOf('day').valueOf();
                      const pxPerMs = canvasWidth / totalCanvasDuration;

                      // 2. Compute the distance in milliseconds from the marker to the end of the visible view
                      const durationToEndOfView = firstStart - dayjs(currentDate).startOf('day').valueOf();

                      // 3. Convert duration to pixels
                      const widthInPixels = durationToEndOfView * pxPerMs;

                      // Ensure we don't return negative widths if the marker is off-screen past the right side
                      const safeWidth = Math.max(0, widthInPixels);

                      return (
                        <CustomMarker key={Math.random()} date={dayjs(firstStart).startOf('day').valueOf()}>
                          {({ styles }) => {
                            const customStyles = {
                              ...styles,
                              backgroundColor: 'rgba(120, 116, 116, 0.65)', // Your empty background color
                              top: `${index * 30}px`,           // Align vertically with the row
                              height: `${30}px`,
                              width: `${safeWidth}px`,
                              pointerEvents: 'none',                   // Ensure clicks still pass through to canvas
                              zIndex: 0                                // Keep it underneath timeline items
                            };
                            return <div style={customStyles} />;
                          }}
                        </CustomMarker>
                      );
                    })}
                    {canvasWidth && groupData.map((group, index) => {
                      const firstStart = dayjs(firstItemStartTimes[group.id]).valueOf();
                      return (
                        <>
                        {dayjs(dayjs(currentDate)).isBefore(firstStart, 'day') ? <CustomMarker key={Math.random()} date={dayjs(currentDate).valueOf()}>
                          {({ styles }) => {
                            // 2. Adjust styles to turn the marker vertical line into a block
                            const customStyles = {
                              ...styles,
                              backgroundColor: 'rgba(120, 116, 116, 0.65)', // Your empty background color
                              top: `${index * 30}px`,           // Align vertically with the row
                              height: `${30}px`,
                              left: `0px`,
                              width: `${canvasWidth}px`, 
                              pointerEvents: 'none',                   // Ensure clicks still pass through to canvas
                              zIndex: 0                                // Keep it underneath timeline items
                            };
                            return <div style={customStyles} />;
                          }}
                        </CustomMarker> : ""}
                        </>
                      );
                    })}
                  </TimelineMarkers>
                </Timeline>
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      </div>
    </div>}
    {!selectedAnimal || (selectedAnimal && data.vet_requests.filter(vr => (Number(vr.animal_object.id) === Number(selectedAnimal.id)))).length > 0 ? <div className="row mt-3 mb-2">
      <div className="col-12 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginTop:"-10px"}}>
            <div className="d-flex justify-content-between" style={{marginBottom:"-3px"}}>
              <h4 className="h4 mb-0 pb-0 pt-2">
                Open Veterinary Requests
                <FontAwesomeIcon icon={faChevronCircleUp} hidden={!showRequests} onClick={() => {setShowRequests(false)}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                <FontAwesomeIcon icon={faChevronCircleDown} hidden={showRequests} onClick={() => {setShowRequests(true)}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
              </h4>
            </div>
            <Collapse in={showRequests}>
              <div>
                <p className="phr" />
                <Scrollbar style={{height:"180px", minHeight:"180px"}} renderView={props => <div {...props} style={{...props.style, overflowX:"hidden", marginBottom:"-10px"}}/>}  renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                {Object.keys(animalData).filter(animal_id => activeOrders === 'vet_requests' ? animalData[animal_id].vet_request > 0 : animal_id)
                .filter(animal_id => selectedAnimal ? Number(selectedAnimal.id) === Number(animal_id) : animal_id)
                .filter(animal_id => vetStatus ? animalData[animal_id].vet_status === vetStatus : animal_id)
                .filter(animal_id => selectedShelter === 'field' ? animalData[animal_id].shelter === null : selectedShelter !== 'all' ? animalData[animal_id].shelter === selectedShelter : animal_id).map((animal_id) => (
                  <Link href={"/" + organization + "/" + incident + "/vet/medrecord/" + animalData[animal_id].medical_record} className="underline-link" style={{color:"white", textDecoration: "none"}} key={animal_id}>
                    <div className="mt-1 mb-1">
                      <div className="card-header border rounded ps-2" style={{fontSize:"20px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                        {activeOrders === 'vet_requests' && data.vet_requests.filter(vr => (Number(vr.animal_object.id) === Number(animal_id)) && vr.priority === 'urgent').length > 0 ?
                        <OverlayTrigger
                          key={"urgent"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-urgent`}>
                              This animal has been marked with an Urgent (Red) priority.
                            </Tooltip>
                          }
                        >
                          <span className="fa-layers ms-1">
                            <FontAwesomeIcon icon={faCircle} className="me-1 fa-move-up" size="sm" style={{color:"white"}} transform="shrink-4" inverse />
                            <FontAwesomeIcon icon={faDiamondExclamation} className="me-1 fa-move-up" style={{color:"#ff4c4c"}} inverse />
                          </span>
                        </OverlayTrigger> : ""}
                        {activeOrders === 'vet_requests' && data.vet_requests.filter(vr => (Number(vr.animal_object.id) === Number(animal_id)) && vr.caution === true).length > 0 ?
                        <OverlayTrigger
                          key={"caution"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-caution`}>
                              Use caution when handling this animal.
                            </Tooltip>
                          }
                        >
                          <span className="fa-layers me-1">
                            <FontAwesomeIcon icon={faCircle} className="me-1 fa-move-down" size="sm" style={{color:"white", marginLeft:"3px"}} transform="left-1 shrink-6" inverse />
                            <FontAwesomeIcon icon={faTriangleExclamation} className="ms-0 me-1" style={{color:"#f2c81f"}} inverse />
                          </span>
                        </OverlayTrigger> : ""}
                        A#{animalData[animal_id].id_for_incident} - {capitalize(animalData[animal_id].species_string)} - {animalData[animal_id].animal_count === 1 ? <span>{animalData[animal_id].name || 'Unknown Name'} - </span> : <span>{animalData[animal_id].animal_count} <span style={{textTransform:"capitalize"}}>({animalData[animal_id].species_string}) -&nbsp;</span></span>}
                        {activeOrders === 'vet_requests' && animalData[animal_id].vet_request ? <span style={{textTransform:'capitalize'}}>{data.vet_requests.filter(vr => Number(vr.animal_object.id) === Number(animal_id)).map(vr => vr.complaints_text).join(", ")}</span> : ""}
                      </div>
                    </div>
                  </Link>
                ))}
                {activeOrders === 'vet_requests' && data.vet_requests.filter(vr => selectedShelter === 'field' ? vr.animal_object.shelter === null : selectedShelter !== 'all' ? vr.animal_object.shelter === selectedShelter : vr).length === 0 ? <span>No animals require examination at this time.</span> : ""}
                </Scrollbar>
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      </div>
    </div> : ""}
    </>
  )
}

export default Vet
